import { ACTAS_ROSTER } from '../data/roster-config'
import { rosterShift } from '../data/roster-shifts'
import { addDays } from '../engine/civil'
import { effectiveRoster } from '../engine/effective-roster'
import type { BlockCode, IsoDate } from '../engine/types'
import { formatShortDate, formatTimeRange } from '../lib/format-date'
import { Button, ShiftList } from '../ui'
import { RosterShiftRow } from './RosterShiftRow'

export interface FourWeekConfirmProps {
  block: BlockCode
  /** Passed in; this component never reads the clock. */
  today: IsoDate
  onConfirm: () => void
  onBack: () => void
}

/** 28 days — "the next four weeks", PLAN.md §8's own words for this screen. */
const WINDOW_DAYS = 28

/**
 * The trust moment (PLAN.md §8): the first time the app shows someone their
 * own roster and asks them to check it. Everything else in the app is only
 * believable if this screen is right, so the heading asks the one question
 * this screen exists to answer, in plain words, and the two actions below
 * answer it — "yes, this is right" or "no, change my block" — rather than a
 * generic Confirm/Back that leaves the paramedic to work out what they are
 * agreeing to.
 *
 * The count is never hard-coded. 28 days is three whole 8-day cycles plus
 * four extra days, and those four can land anywhere from all-working to
 * all-off depending on where the window starts, so the real row count runs
 * 12–16 — whatever `effectiveRoster` returns for this exact window, passed
 * straight to `ShiftList`'s `count`.
 */
export function FourWeekConfirm({ block, today, onConfirm, onBack }: FourWeekConfirmProps) {
  const range = { from: today, to: addDays(today, WINDOW_DAYS - 1) }
  const shifts = effectiveRoster({ block, range, exceptions: [], explicit: [] }, ACTAS_ROSTER)

  return (
    <div className="sl-stack">
      <div>
        <h1 className="sl-heading">Does this match your roster?</h1>
        <p className="sl-hint">
          This is your next four weeks on block {block}, worked out from the roster pattern — not
          typed in by hand. Check it against the roster you already know. If something's wrong,
          go back and pick a different block.
        </p>
      </div>

      <ShiftList title="Your next four weeks" count={shifts.length}>
        {shifts.map((shift) => {
          const times = rosterShift(shift.code)
          return (
            <RosterShiftRow
              key={`${shift.date}-${shift.code}`}
              date={formatShortDate(shift.date)}
              code={shift.code}
              timeRange={formatTimeRange(times.startMin, times.endMin)}
            />
          )
        })}
      </ShiftList>

      <div className="ss-confirm-actions">
        <Button variant="primary" block onClick={onConfirm}>
          Yes, this is right
        </Button>
        <Button variant="secondary" block onClick={onBack}>
          No, change my block
        </Button>
      </div>
    </div>
  )
}
